import React, {Fragment, useState} from 'react';

import cs from 'classnames';

import {Mentionable} from 'app/components/general/Mentionable';
import {ImageAttachmentsList} from 'app/general/uploader/components/ImageAttachmentsList';
import {ImageAttachmentsUploader} from 'app/general/uploader/components/ImageAttachmentsUploader';
import {ImagePreview} from 'app/general/uploader/components/ImagePreview';
import {useAttachmentsUploader} from 'app/general/uploader/hooks';

import {CommentTextForm} from './CommentTextForm';
import {HideDeletedCommentsCheckbox} from './HideDeletedCommentsCheckbox';

import {BaseAttachedImage, CommentContentType} from 'sweet-types';

type Props = {
  onSubmit: (
    commentData: string,
    attachments: Array<BaseAttachedImage>
  ) => Promise<unknown>;
  className?: string;
  buttonClass?: string;
  inputClass?: string;
  contentType: CommentContentType;
  objectId: string | number;
  hideTooltip?: boolean;
  showDeletedCheckbox?: boolean;
};

export function CommentFormWithUploader({
  inputClass = '',
  buttonClass = '',
  className = '',
  onSubmit,
  contentType,
  objectId,
  hideTooltip,
  showDeletedCheckbox = true,
  ...other
}: Props) {
  const {
    uploaderRef,
    attachments,
    submittedImages,
    onImageUpload,
    setSubmittedImages,
    cancelUploadImage,
    removeAttachment,
    reset,
  } = useAttachmentsUploader();

  const [comment, setComment] = useState<string>('');
  const [idle, setIdle] = useState<boolean>(true);

  function handleSubmit(
    event:
      | React.SyntheticEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLDivElement>,
    currentComment?: string
  ) {
    const newComment = currentComment != null ? currentComment : comment;
    event.preventDefault();
    setComment('');
    setIdle(false);
    onSubmit(newComment, attachments).then(
      () => {
        setIdle(true);
        reset();
      },
      () => setIdle(true)
    );
  }

  return (
    <Mentionable
      contentType={contentType}
      objectId={objectId}
      onChange={(comment: string) => setComment(comment)}
      onSubmit={handleSubmit}
      value={comment}
    >
      {({Field, fieldProps, mentionsMenu}) => (
        <Fragment>
          {mentionsMenu}
          <form {...other} onSubmit={handleSubmit}>
            <div
              className={cs('d-flex', 'align-items-center', {
                'justify-content-between': showDeletedCheckbox,
                'justify-content-end': !showDeletedCheckbox,
              })}
            >
              <HideDeletedCommentsCheckbox show={showDeletedCheckbox} />
              {attachments.length === 0 && (
                <ImageAttachmentsUploader
                  uploaderRef={uploaderRef}
                  setSubmittedImages={setSubmittedImages}
                  onAddImage={onImageUpload}
                />
              )}
            </div>
            {attachments.length > 0 && (
              <ImagePreview
                attachment={attachments[0]}
                onRemoveAttachment={removeAttachment}
              />
            )}
            <CommentTextForm
              className={className}
              inputClass={inputClass}
              buttonClass={buttonClass}
              submitButtonDisabled={!comment && attachments.length === 0}
              isIdle={idle}
              MentionableField={Field}
              mentionableFieldProps={fieldProps}
              hideTooltip={hideTooltip ?? false}
            />
            <ImageAttachmentsList
              uploaderRef={uploaderRef}
              submittedImages={submittedImages}
              onCancelImage={cancelUploadImage}
            />
          </form>
        </Fragment>
      )}
    </Mentionable>
  );
}
